/**
 * 支払い先マスタ(vendors)と登録依頼(vendor_requests)。
 * 申請フォームは選択のみ(自由入力禁止)。新規は経理へ登録依頼するフロー。
 * counterparty からの自動生成で表記ゆれ重複が出るため、マージ機能を持つ(補足指示C)。
 */
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { nextMasterCode } from '@core/lib/numbering';
import { writeAuditLog } from '@core/lib/auditLog';
import type { Vendor, VendorRequest } from '@core/lib/types';

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

export async function fetchVendors(includeInactive = false): Promise<Vendor[]> {
  const snap = await getDocs(query(collection(getDb(), 'vendors'), orderBy('name')));
  return snap.docs.map((d) => withId<Vendor>(d)).filter((v) => includeInactive || v.active);
}

export async function createVendor(
  input: { name: string; bankInfo: string; note: string },
  userId: string,
): Promise<string> {
  const name = input.name.trim();
  if (!name) throw new Error('支払い先の名称を入力してください');
  const code = await nextMasterCode('V');
  const ref = doc(collection(getDb(), 'vendors'));
  await setDoc(ref, {
    code,
    name,
    bank_info: input.bankInfo.trim() || null,
    note: input.note.trim() || null,
    active: true,
    created_by: userId,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  await writeAuditLog({
    targetType: 'master',
    targetId: ref.id,
    action: 'vendor-create',
    by: userId,
    after: { code, name },
  });
  return ref.id;
}

export async function updateVendor(
  id: string,
  patch: { name: string; bankInfo: string; note: string; active: boolean },
): Promise<void> {
  if (!patch.name.trim()) throw new Error('名称を入力してください');
  await updateDoc(doc(getDb(), 'vendors', id), {
    name: patch.name.trim(),
    bank_info: patch.bankInfo.trim() || null,
    note: patch.note.trim() || null,
    active: patch.active,
    updated_at: serverTimestamp(),
  });
}

/**
 * マージ: 統合元(source)に紐づく取引・固定費をすべて統合先(target)へ付け替え、
 * 統合元は無効化する(削除はしない。管理番号の履歴を残すため)。
 */
export async function mergeVendors(params: {
  sourceId: string;
  targetId: string;
  userId: string;
}): Promise<{ moved: number }> {
  const { sourceId, targetId, userId } = params;
  if (sourceId === targetId) throw new Error('統合元と統合先が同じです');
  const db = getDb();

  const txSnap = await getDocs(
    query(collection(db, 'transactions'), where('vendor_id', '==', sourceId)),
  );
  let moved = 0;
  for (let i = 0; i < txSnap.docs.length; i += 400) {
    const batch = writeBatch(db);
    for (const d of txSnap.docs.slice(i, i + 400)) {
      batch.update(d.ref, { vendor_id: targetId });
      moved++;
    }
    await batch.commit();
  }

  await updateDoc(doc(db, 'vendors', sourceId), {
    active: false,
    note: `統合済み(→ ${targetId})`,
    updated_at: serverTimestamp(),
  });

  await writeAuditLog({
    targetType: 'master',
    targetId: sourceId,
    action: 'vendor-merge',
    by: userId,
    after: { target: targetId, moved },
  });
  return { moved };
}

// --- 登録依頼 ---------------------------------------------------------------

export async function fetchVendorRequests(openOnly = true): Promise<VendorRequest[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'vendor_requests'), orderBy('requested_at', 'desc')),
  );
  return snap.docs
    .map((d) => withId<VendorRequest>(d))
    .filter((r) => !openOnly || r.status === 'open');
}

/** 申請者からの登録依頼(申請フォームの「＋新しい支払い先の登録を依頼」) */
export async function createVendorRequest(
  input: { name: string; note: string },
  userId: string,
): Promise<void> {
  if (!input.name.trim()) throw new Error('支払い先の名称を入力してください');
  await setDoc(doc(collection(getDb(), 'vendor_requests')), {
    name: input.name.trim(),
    note: input.note.trim() || null,
    status: 'open',
    requested_by: userId,
    requested_at: serverTimestamp(),
    resolved_by: null,
    resolved_at: null,
    vendor_id: null,
  });
}

/** 依頼を承認して vendors に登録する(経理) */
export async function resolveVendorRequest(request: VendorRequest, userId: string): Promise<void> {
  const vendorId = await createVendor({ name: request.name, bankInfo: '', note: request.note ?? '' }, userId);
  await updateDoc(doc(getDb(), 'vendor_requests', request.id), {
    status: 'done',
    resolved_by: userId,
    resolved_at: serverTimestamp(),
    vendor_id: vendorId,
  });
}

export async function dismissVendorRequest(id: string, userId: string): Promise<void> {
  await updateDoc(doc(getDb(), 'vendor_requests', id), {
    status: 'dismissed',
    resolved_by: userId,
    resolved_at: serverTimestamp(),
  });
}
